import { ArrowPath, ArrowRightOnRectangle, XMark } from '@medusajs/icons';
import { Badge, Button, Prompt, Skeleton, StatusBadge, Text, toast } from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { SectionContainer, SectionEmpty, SectionHeader } from '../../components/section';
import { ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useHasPermission } from '../auth/use-session';
import type { Company } from './companies.api';
import {
  disconnectSpv,
  renewSpvNow,
  type SpvConnection,
  type SpvStatus,
  spvQueryKey,
  spvQueryOptions,
  startSpvConnection,
} from './spv.api';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * De la câte zile înainte de expirarea tokenului de reînnoire spune ecranul că trebuie
 * reconectat. Același prag ca primul avertisment din clopoțel și pe email (30, 14, 7 zile).
 */
const RECONNECT_WARNING_DAYS = 30;

/** Ce a întors callback-ul ANAF, în adresa pe care s-a întors browserul. */
export interface SpvCallbackResult {
  readonly outcome: 'connected' | 'refused' | 'unreachable';
  readonly message: string | undefined;
}

/**
 * Tabul „SPV" al firmei ([#200](https://github.com/bplugaru/efx-pcrm/issues/200)): conectarea
 * cu certificatul, cine s-a conectat, când expiră tokenurile, și ce e de făcut.
 *
 * **Conectarea nu se poate face din aplicație, doar porni de aici**: „Conectează la SPV" trimite
 * browserul la ANAF, unde omul alege certificatul de pe tokenul USB și tastează PIN-ul. ANAF îl
 * întoarce aici, iar serverul a schimbat deja codul pe tokenuri.
 *
 * ⚠️ **Tokenurile nu ajung niciodată în browser** — nici aici, nici în altă parte.
 */
export function CompanySpvSection({
  company,
  callback,
  onCallbackSeen,
}: {
  company: Company;
  callback: SpvCallbackResult | null;
  onCallbackSeen: () => void;
}) {
  const query = useQuery(spvQueryOptions(company.id));
  const can = useHasPermission();
  const canManage = can('companies.manage');

  // Rezultatul conectării se spune o dată, apoi iese din adresă: un refresh nu-l mai repetă.
  useEffect(() => {
    if (callback === null) {
      return;
    }

    if (callback.outcome === 'connected') {
      toast.success('Firma e conectată la SPV');
    } else {
      toast.error(
        callback.outcome === 'refused'
          ? `ANAF a refuzat conectarea${callback.message ? `: ${callback.message}` : '.'}`
          : `ANAF nu a răspuns${callback.message ? `: ${callback.message}` : '.'} Încercați din nou.`,
      );
    }

    onCallbackSeen();
  }, [callback, onCallbackSeen]);

  if (query.isPending) {
    return (
      <SectionContainer>
        <div className="px-6 py-6">
          <Skeleton className="h-16 w-full" />
        </div>
      </SectionContainer>
    );
  }

  if (query.error) {
    return (
      <SectionContainer>
        <SectionEmpty tone="error">
          {query.error instanceof Error ? query.error.message : 'Starea SPV nu a putut fi citită.'}
        </SectionEmpty>
      </SectionContainer>
    );
  }

  const status = query.data;

  return (
    <SectionContainer>
      <SectionHeader
        title={
          <span className="flex items-center gap-x-2">
            Conectarea la SPV
            <SpvStateBadge status={status} />
            <EnvironmentBadge status={status} />
          </span>
        }
        subtitle="Tokenul OAuth ANAF cu care se vor urca facturile firmei în SPV."
      />

      {status.configured ? (
        <div className="flex flex-col gap-y-4 px-6 py-4">
          {status.connection ? (
            <ConnectionDetails connection={status.connection} />
          ) : (
            <Text size="small" className="text-ui-fg-subtle">
              Firma nu e conectată la SPV. Conectarea o face cineva cu certificat digital calificat
              și drept de PJ în SPV pe această firmă.
            </Text>
          )}

          {canManage ? <SpvActions company={company} status={status} /> : null}

          {status.redirectUri ? (
            <Text size="small" className="text-ui-fg-subtle">
              Callback URL-ul trimis la ANAF, de pus exact așa în profilul OAuth al aplicației:{' '}
              <code className="break-all text-ui-fg-base">{status.redirectUri}</code>
            </Text>
          ) : null}

          <Text size="small" className="text-ui-fg-subtle">
            Tokenul e al certificatului, nu al aplicației: acoperă doar CUI-urile pe care le
            reprezintă certificatul în SPV. Dacă aceeași persoană reprezintă mai multe firme, se
            conectează la fiecare.
          </Text>
        </div>
      ) : (
        <div className="flex flex-col gap-y-2 px-6 py-4">
          <Text size="small" weight="plus">
            SPV nu e configurat pe acest server.
          </Text>
          {status.enabled ? (
            <Text size="small" className="text-ui-fg-subtle">
              Lipsesc Client ID-ul și Client Secret-ul aplicației ANAF. Se completează în{' '}
              <Link to="/setari/spv" className="text-ui-fg-interactive underline">
                Setări → ANAF (SPV)
              </Link>
              .
            </Text>
          ) : (
            <Text size="small" className="text-ui-fg-subtle">
              Serverul n-are <code>SPV_ENCRYPTION_KEY</code> în mediu. Restul aplicației merge
              normal.
            </Text>
          )}
        </div>
      )}
    </SectionContainer>
  );
}

function SpvStateBadge({ status }: { status: SpvStatus }) {
  if (!status.configured) {
    return null;
  }

  if (status.connection === null) {
    return <StatusBadge color="grey">Neconectată</StatusBadge>;
  }

  return status.connection.state === 'connected' ? (
    <StatusBadge color="green">Conectată</StatusBadge>
  ) : (
    <StatusBadge color="red">De reconectat</StatusBadge>
  );
}

/** Mediul e-Factura spre care vor pleca facturile: test pe serverul de dev, real în producție. */
function EnvironmentBadge({ status }: { status: SpvStatus }) {
  if (status.environment === null) {
    return null;
  }

  return status.environment === 'prod' ? (
    <Badge size="2xsmall" color="blue">
      ANAF producție
    </Badge>
  ) : (
    <Badge size="2xsmall" color="orange" title={status.eFacturaUrl ?? undefined}>
      {status.environment === 'test' ? 'ANAF test' : 'ANAF: altă adresă'}
    </Badge>
  );
}

function ConnectionDetails({ connection }: { connection: SpvConnection }) {
  const daysLeft = Math.max(
    1,
    Math.ceil((Date.parse(connection.refreshExpiresAt) - Date.now()) / DAY_MS),
  );
  const rows: readonly (readonly [string, string])[] = [
    ['Conectată de', connection.connectedBy],
    ['Conectată la', formatDateTime(connection.connectedAt)],
    ['Accesul expiră', formatDateTime(connection.accessExpiresAt)],
    ['Reconectare cu certificatul până la', formatDateTime(connection.refreshExpiresAt)],
    [
      'Ultima reînnoire',
      connection.refreshedAt ? formatDateTime(connection.refreshedAt) : 'încă niciuna',
    ],
  ];

  return (
    <div className="flex flex-col gap-y-3">
      {connection.state === 'reconnect' ? (
        <Text size="small" className="text-ui-fg-error">
          Conexiunea trebuie refăcută cu certificatul
          {connection.lastError ? `: ${connection.lastError}` : '.'} Până atunci, facturile nu pot
          fi urcate în SPV.
        </Text>
      ) : null}

      {connection.state === 'connected' && daysLeft <= RECONNECT_WARNING_DAYS ? (
        <Text size="small" className="text-ui-fg-error">
          Tokenul de reînnoire expiră în {daysLeft === 1 ? 'o zi' : `${daysLeft} zile`}. Reconectați
          firma cu certificatul înainte, altfel facturile nu se mai pot urca în SPV.
        </Text>
      ) : null}

      <dl className="grid max-w-2xl grid-cols-[auto_1fr] gap-x-6 gap-y-1">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt>
              <Text size="small" className="text-ui-fg-subtle">
                {label}
              </Text>
            </dt>
            <dd>
              <Text size="small">{value}</Text>
            </dd>
          </div>
        ))}
      </dl>

      {connection.state === 'connected' && connection.lastError ? (
        <Text size="small" className="text-ui-fg-subtle">
          Ultima încercare de reînnoire n-a ajuns la ANAF
          {connection.lastErrorAt ? ` (${formatDateTime(connection.lastErrorAt)})` : ''}:{' '}
          {connection.lastError} Se reîncearcă singură.
        </Text>
      ) : null}
    </div>
  );
}

function SpvActions({ company, status }: { company: Company; status: SpvStatus }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const connection = status.connection;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: spvQueryKey(company.id) });
  const onError = (error: unknown) =>
    toast.error(error instanceof ApiError ? error.message : 'Cererea nu a reușit.');

  const connect = useMutation({
    mutationFn: () => startSpvConnection(company.id),
    // Browserul pleacă la ANAF; se întoarce pe fișa asta, prin callback.
    onSuccess: (url) => window.location.assign(url),
    onError,
  });

  const renew = useMutation({
    mutationFn: () => renewSpvNow(company.id),
    onSuccess: async (result) => {
      if (result.result === 'renewed') {
        toast.success('Tokenul de acces a fost reînnoit');
      } else if (result.result === 'refused') {
        toast.error(`ANAF a refuzat reînnoirea: ${result.message ?? ''}`);
      } else if (result.result === 'unreachable') {
        toast.error(`ANAF nu a răspuns: ${result.message ?? ''}`);
      }

      await invalidate();
    },
    onError,
  });

  const disconnect = useMutation({
    mutationFn: () => disconnectSpv(company.id),
    onSuccess: async () => {
      toast.success('Tokenurile au fost șterse din aplicație');
      await invalidate();
    },
    onError,
  });

  const busy = connect.isPending || renew.isPending || disconnect.isPending;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        size="small"
        variant={connection?.state === 'connected' ? 'secondary' : 'primary'}
        onClick={() => connect.mutate()}
        isLoading={connect.isPending}
        disabled={busy}
      >
        <ArrowRightOnRectangle />
        {connection === null ? 'Conectează la SPV' : 'Reconectează'}
      </Button>

      {connection?.state === 'connected' ? (
        <Button
          size="small"
          variant="secondary"
          onClick={() => renew.mutate()}
          isLoading={renew.isPending}
          disabled={busy}
        >
          <ArrowPath />
          Reînnoiește acum
        </Button>
      ) : null}

      {connection !== null ? (
        <Button
          size="small"
          variant="transparent"
          onClick={() => setConfirming(true)}
          disabled={busy}
        >
          <XMark />
          Deconectează
        </Button>
      ) : null}

      <Prompt open={confirming} onOpenChange={setConfirming} variant="danger">
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Deconectezi firma de la SPV?</Prompt.Title>
            <Prompt.Description>
              Tokenurile se șterg din aplicație, iar facturile nu se mai pot urca până la o nouă
              conectare cu certificatul. Tokenul rămâne valabil la ANAF: revocarea se cere prin
              formularul de contact ANAF, nu dintr-un buton.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Renunță</Prompt.Cancel>
            <Prompt.Action
              onClick={() => {
                disconnect.mutate();
                setConfirming(false);
              }}
            >
              Deconectează
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </div>
  );
}
